import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthMe } from "../../../../../api/requests/auth-requests";
import { fetchFinalRates } from "../../../../../api/requests/payment-requests";
import { fetchAccountsMe } from "../../../../../api/requests/account-requests";

export const usePaymentSession = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [profile, setProfile] = useState({ domain: '', userName: '', email: '', userPhone: '', userAddress: '' });
    const [finalRates, setFinalRates] = useState({ usdFinalRate: null, eurFinalRate: null, gbpFinalRate: null });
    const [pageLoading, setPageLoading] = useState(true);
    const navigate = useNavigate();

    const isAccountRole = currentUser?.roles?.some(r =>
        r === "ROLE_ACCOUNT_ACCOUNTANT" || r === "ROLE_ACCOUNT_ADMIN"
    );

    // Cari muhasebe (CARİ/BAYİ MUHASEBE) kullanıcısı: bu role sadece anlık ödeme
    // bırakılır; kendi carisine bağlı "cari ile ödeme" akışı gizlenir.
    // (ROLE_ACCOUNT_ADMIN bu kısıttan etkilenmez.)
    const isAccountAccountant = currentUser?.roles?.includes("ROLE_ACCOUNT_ACCOUNTANT");

    useEffect(() => {

        const fetchUserInfo = async () => {
            try {
                setPageLoading(true);
                const response = await fetchAuthMe();

                // GÜNCELLEME: auth_me için de hata yönetimi eklendi
                if (response.status === 401 || response.status === 403) {
                    console.error("Auth me error: 401/403");
                    navigate("/login");
                    return;
                }
                if (!response.ok) throw new Error('Auth failed');

                const { data } = await response.json();
                setCurrentUser(data);
                setCompanyId(data.companyId);
                setProfile({
                    domain: window.location.hostname,
                    userName: data.username || data.fullName,
                    email: data.email,
                    userPhone: data.phoneNumber || '',
                    userAddress: data.address || ''
                });
            } catch (error) {
                console.error("Auth me error:", error);
                navigate("/login");
            } finally {
                // Not: Bu finally, fetchAccounts bitmeden tetiklenebilir.
                // İki fetch'in de bitmesini beklemek en doğrusu olurdu.
                setPageLoading(false);
            }
        };
        fetchUserInfo();
    }, [navigate]);

    // Fetch Accounts (accounts_me)
    useEffect(() => {
        // GÜNCELLEME 1: 'isAccountRole' kontrolü kaldırıldı.
        // Artık rolü olmayan kullanıcılar için de (opsiyonel seçim yapabilmeleri için)
        // cari hesap listesi çekilecek.
        const fetchAccounts = async () => {
            try {
                const res = await fetchAccountsMe();

                // GÜNCELLEME 2: 401/403 (Yetki) hatası yönetimi eklendi.
                if (res.status === 401 || res.status === 403) {
                    console.error("Fetch accounts error: 401/403");
                    navigate("/login");
                    return;
                }

                if (!res.ok) throw new Error(`Status ${res.status}`);

                const { data } = await res.json();
                setAccounts(data);
            } catch (err) {
                console.error("Fetch accounts error:", err);
                // GÜNCELLEME 3: Genel hata durumunda login'e yönlendirme eklendi.
                navigate("/login");
            }
        };
        fetchAccounts();
    }, [navigate]);

    // Fetch Final Rates
    useEffect(() => {
        if (!companyId) return;

        const loadFinalRates = async () => {
            try {
                const res = await fetchFinalRates(companyId);

                // Not: Buraya da 401/403 yönlendirmesi eklemek isteyebilirsiniz.
                if (res.status === 401 || res.status === 403) {
                    navigate("/login");
                    return;
                }

                if (!res.ok) throw new Error('Final rates fetch failed');
                const { data } = await res.json();
                setFinalRates(data || { usdFinalRate: null, eurFinalRate: null, gbpFinalRate: null });
            } catch (err) {
                console.error("Final rates request error:", err);
                // Buraya da navigate("/login") ekleyebilirsiniz.
            }
        };
        loadFinalRates();
    }, [companyId, navigate]);

    return {
        currentUser,
        companyId,
        accounts,
        profile,
        finalRates,
        pageLoading,
        isAccountRole,
        isAccountAccountant
    };
};